import { PublicImage, ResponsiveImage } from '../modules/fileStorage/ResponsiveImage';
import { useMemo, useState } from 'react';

export interface CatalogCategoryMenuItem {
  category_image?: PublicImage | null;
  category_id: number;
  category_name: string;
  product_count: number;
  children: CatalogCategoryMenuItem[];
}

interface CatalogCategoryMenuProps {
  categories: CatalogCategoryMenuItem[];
  onSelect: (categoryId: number | null) => void;
  selectedCategoryId: number | null;
  totalCount: number;
}

interface FlatCategoryItem {
  category: CatalogCategoryMenuItem;
  path: string[];
}

export function CatalogCategoryMenu({
  categories,
  onSelect,
  selectedCategoryId,
  totalCount,
}: CatalogCategoryMenuProps) {
  const [query, setQuery] = useState('');
  const flatCategories = useMemo(() => flattenCategories(categories), [categories]);
  const normalizedQuery = query.trim().toLocaleLowerCase('ru-RU');
  const searchResults = normalizedQuery === ''
    ? []
    : flatCategories.filter(({ category }) => (
      category.category_name.toLocaleLowerCase('ru-RU').includes(normalizedQuery)
    ));
  const selectedCategory = flatCategories.find(({ category }) => category.category_id === selectedCategoryId) ?? null;

  const selectCategory = (categoryId: number | null): void => {
    setQuery('');
    onSelect(categoryId);
  };

  return (
    <aside className="catalog-category-menu" aria-label="Категории каталога">
      <header className="catalog-category-menu__header">
        <div>
          <span>Навигация</span>
          <h2>Категории</h2>
        </div>
        <b>{formatCategoryCount(flatCategories.length)}</b>
      </header>

      <label className="catalog-category-search">
        <span className="catalog-category-search__icon" aria-hidden="true">⌕</span>
        <input
          aria-label="Поиск категории"
          autoComplete="new-password"
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Найти категорию"
          type="search"
          value={query}
        />
        {query !== '' ? (
          <button aria-label="Очистить поиск категорий" onClick={() => setQuery('')} type="button">×</button>
        ) : null}
      </label>

      {selectedCategory !== null && normalizedQuery === '' ? (
        <div className="catalog-category-menu__selection">
          <span>Выбрано</span>
          <strong>{selectedCategory.path.join(' / ')}</strong>
        </div>
      ) : null}

      <div className="catalog-category-menu__body">
        {normalizedQuery === '' ? (
          <>
            <CategoryButton
              categoryId={null}
              count={totalCount}
              isActive={selectedCategoryId === null}
              label="Все категории"
              onSelect={selectCategory}
            />
            <CategoryTree
              categories={categories}
              depth={0}
              onSelect={selectCategory}
              selectedCategoryId={selectedCategoryId}
            />
          </>
        ) : searchResults.length > 0 ? (
          <div className="catalog-category-results">
            <small>Найдено: {searchResults.length}</small>
            {searchResults.map(({ category, path }) => (
              <button
                className={selectedCategoryId === category.category_id ? 'catalog-category-result is-active' : 'catalog-category-result'}
                key={category.category_id}
                onClick={() => selectCategory(category.category_id)}
                type="button"
              >
                <span>
                  {category.category_image ? <ResponsiveImage image={category.category_image} fallbackSrc={category.category_image.original.url} sizes="40px" className="catalog-category__image" alt="" /> : null}
                  <strong>{category.category_name}</strong>
                  <small>{path.slice(0, -1).join(' / ') || 'Корневая категория'}</small>
                </span>
                <b>{category.product_count}</b>
              </button>
            ))}
          </div>
        ) : (
          <div className="catalog-category-menu__empty">
            <span aria-hidden="true">⌕</span>
            <p>Категория не найдена</p>
            <small>Попробуйте изменить запрос</small>
          </div>
        )}
      </div>
    </aside>
  );
}

function CategoryTree({
  categories,
  depth,
  onSelect,
  selectedCategoryId,
}: {
  categories: CatalogCategoryMenuItem[];
  depth: number;
  onSelect: (categoryId: number | null) => void;
  selectedCategoryId: number | null;
}) {
  return (
    <div className="catalog-category-tree">
      {categories.map((category) => (
        <div className="catalog-category-branch" key={category.category_id}>
          <CategoryButton
            categoryId={category.category_id}
            count={category.product_count}
            depth={depth}
            isActive={selectedCategoryId === category.category_id}
            image={category.category_image}
            label={category.category_name}
            onSelect={onSelect}
          />
          {category.children.length > 0 ? (
            <CategoryTree
              categories={category.children}
              depth={depth + 1}
              onSelect={onSelect}
              selectedCategoryId={selectedCategoryId}
            />
          ) : null}
        </div>
      ))}
    </div>
  );
}

function CategoryButton({
  categoryId,
  count,
  depth = -1,
  isActive,
  label,
  image,
  onSelect,
}: {
  categoryId: number | null;
  count: number;
  depth?: number;
  isActive: boolean;
  label: string;
  image?: PublicImage | null;
  onSelect: (categoryId: number | null) => void;
}) {
  return (
    <button
      className={isActive ? 'catalog-category is-active' : 'catalog-category'}
      onClick={() => onSelect(categoryId)}
      style={{ paddingLeft: depth < 0 ? 12 : 14 + depth * 16 }}
      type="button"
    >
      <span className="catalog-category__marker" aria-hidden="true" />
      <>{image ? <ResponsiveImage image={image} fallbackSrc={image.original.url} sizes="40px" className="catalog-category__image" alt="" /> : null}<span className="catalog-category__name">{label}</span></>
      <b>{count}</b>
    </button>
  );
}

function flattenCategories(categories: CatalogCategoryMenuItem[], parentPath: string[] = []): FlatCategoryItem[] {
  const result: FlatCategoryItem[] = [];

  categories.forEach((category) => {
    const path = [...parentPath, category.category_name];

    result.push({ category, path });
    result.push(...flattenCategories(category.children, path));
  });

  return result;
}

function formatCategoryCount(count: number): string {
  return `${count} кат.`;
}

